import { RefObject, useEffect, useMemo } from "react"
import { parseValidFloat } from "../../../helpers/widget"
import { IFCViewerV4ConfigOptions } from "./config"
import { RemoteProxy } from "penpal"
import { IFrameApi } from "./shared/iframe-api"
import { OpaqueSparqlResults } from "../../../helpers/sparqlResultHelpers"
import { DEFAULT_WIDGET_CONFIG } from "../../default-widget-config"
import { ViewerConfig } from "./shared/viewer-config"

type SyncViewerConf = {
  widgetConfig: IFCViewerV4ConfigOptions
  iframeApiRef: RefObject<RemoteProxy<IFrameApi> | undefined>
  iframeLoadedAt: number
  sparqlResults: OpaqueSparqlResults | undefined
}

export function useSyncViewerConfig(conf: SyncViewerConf) {
  const widgetConfig = conf.widgetConfig
  const defaultConfig = DEFAULT_WIDGET_CONFIG.IFCViewerV4
  const viewerConfig = useMemo(() => {
    return ({
      display: {
        mode: widgetConfig.displayMode ?? "normal",
        transparencyOpacity: widgetConfig.displayMode === "transparent"
          ? parseValidFloat(widgetConfig.transparencyOpacity)
          : undefined,
        predefinedColors: widgetConfig.predefinedColors,
        assignRandomColorsToTags: widgetConfig.assignRandomColorsToTags ?? defaultConfig.assignRandomColorsToTags,
        legendSorting: widgetConfig.legendSorting ?? defaultConfig.legendSorting
      },
      selection: {
        zoomToSelection: Boolean(widgetConfig.zoomToSelection)
      },
      controls: {
        infinityDolly: widgetConfig.infinityDolly ?? defaultConfig.infiniteDolly,
        dollyToCursor: widgetConfig.dollyToCursor ?? defaultConfig.dollyToCursor,
        verticalDragToForward: widgetConfig.verticalDragToForward ?? defaultConfig.verticalDragToForward,
        minDollyDistance: parseValidFloat(widgetConfig.minDollyDistance) ?? defaultConfig.minDollyDistance,
        maxDollyDistance: parseValidFloat(widgetConfig.maxDollyDistance) ?? defaultConfig.maxDollyDistance
      }
    } satisfies ViewerConfig)
  }, [
    widgetConfig.displayMode,
    widgetConfig.transparencyOpacity,
    widgetConfig.predefinedColors,
    widgetConfig.assignRandomColorsToTags,
    widgetConfig.legendSorting,
    widgetConfig.zoomToSelection,
    widgetConfig.infinityDolly,
    widgetConfig.dollyToCursor,
    widgetConfig.verticalDragToForward,
    widgetConfig.minDollyDistance,
    widgetConfig.maxDollyDistance
  ])

  useEffect(function syncViewerConfig() {
    if (!conf.iframeApiRef.current) return
    conf.iframeApiRef.current.setViewerConfig(viewerConfig).catch(console.error)
  }, [ conf.iframeApiRef, conf.iframeLoadedAt, viewerConfig
  ])

  useEffect(function syncSparqlResults() {
    if (!conf.sparqlResults) return
    if (!conf.iframeApiRef.current) return
     conf.iframeApiRef.current.setSparqlResults(conf.sparqlResults)
      .catch(console.error)
  }, [ conf.iframeApiRef, conf.iframeLoadedAt, conf.sparqlResults ])
}
